import { DownOutlined, RightOutlined } from '@ant-design/icons'
import { InputNumber, Radio, Select, Slider } from 'antd'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { VideoGenConfig } from '../../api'
import { AspectRatioIcon, FALLBACK_ASPECT_RATIO_CHOICES } from '../VideoGenerationPage'
import { clampDuration, resolveMaxDurationForQuality } from './videoDirectorLogic'
import type { VideoParametersV1 } from './videoDirectorTypes'

const QUICK_DURATION_PRESETS = [5, 10, 15, 30]

/**
 * Spec §十八/§十九/§二十一 — the sticky "视频参数" panel. Every value here
 * is backed by the real `GET /video-generation/config` capability
 * (`cfg`) plus the real request-time fields
 * `app.saas.services.video_generation_params` already enforces
 * server-side (seed, image/first/last strength). FPS and generation
 * count are NOT user-adjustable in the real backend today — they render
 * as fixed/read-only facts rather than fake dropdowns (spec §十八/§十九:
 * "不要为了 UI fake 参数").
 */
export default function VideoParametersPanel({
  cfg,
  value,
  onChange,
  hasReferenceImage,
  hasReferenceVideo,
}: {
  cfg: VideoGenConfig | null
  value: VideoParametersV1
  onChange: (next: VideoParametersV1) => void
  hasReferenceImage: boolean
  hasReferenceVideo: boolean
}) {
  const { t } = useTranslation('video')
  const [advancedOpen, setAdvancedOpen] = useState(false)

  const aspectRatioChoices = cfg?.aspect_ratio_choices ?? FALLBACK_ASPECT_RATIO_CHOICES
  const qualityTierChoices = cfg?.quality_tier_choices ?? [
    { value: '720P', label: t('director.parameters.quality720') },
    { value: '1080P', label: t('director.parameters.quality1080') },
  ]
  const maxDurationForQuality = useMemo(
    () => resolveMaxDurationForQuality(cfg, value.quality_tier),
    [cfg, value.quality_tier],
  )

  const patch = (partial: Partial<VideoParametersV1>) => onChange({ ...value, ...partial })

  const setDuration = (next: number) => {
    patch({ duration_seconds: clampDuration(next, maxDurationForQuality) })
  }

  const seedRange = cfg?.seed_range ?? { min: 0, max: 2147483647 }

  return (
    <div className="tc-vdc-params-panel">
      <div className="tc-vdc-params-title">{t('director.parameters.title')}</div>

      <div className="tc-vdc-params-group-label">{t('director.parameters.basic')}</div>

      <div className="tc-vdc-param-field">
        <label>{t('director.parameters.aspectRatio')}</label>
        <div className="tc-vdc-ratio-options">
          {aspectRatioChoices.map((r) => (
            <button
              key={r}
              type="button"
              className={`tc-vdc-ratio-option${value.aspect_ratio === r ? ' is-active' : ''}`}
              onClick={() => patch({ aspect_ratio: r })}
            >
              <AspectRatioIcon ratio={r} size={16} />
              <span>{r}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="tc-vdc-param-field">
        <label>{t('director.parameters.durationSeconds')}</label>
        <div className="tc-vdc-duration-row">
          <InputNumber
            min={1}
            max={maxDurationForQuality}
            value={value.duration_seconds}
            onChange={(v) => setDuration(Number(v) || 1)}
            style={{ width: 84 }}
          />
          <div className="tc-vdc-duration-presets">
            {QUICK_DURATION_PRESETS.map((d) => (
              <button
                key={d}
                type="button"
                disabled={d > maxDurationForQuality}
                className={`tc-vdc-duration-preset${value.duration_seconds === d ? ' is-active' : ''}`}
                onClick={() => setDuration(d)}
              >
                {d}s
              </button>
            ))}
          </div>
        </div>
        <div className="tc-vdc-param-hint">{t('director.parameters.maxDurationHint', { seconds: maxDurationForQuality })}</div>
      </div>

      <div className="tc-vdc-param-field">
        <label>{t('director.parameters.resolution')}</label>
        <Select
          value={value.quality_tier}
          onChange={(v) => patch({ quality_tier: v })}
          style={{ width: '100%' }}
          options={qualityTierChoices}
        />
      </div>

      <div className="tc-vdc-param-field">
        <label>{t('director.parameters.frameRate')}</label>
        <div className="tc-vdc-param-readonly">{t('director.parameters.frameRateFixed', { rate: value.frame_rate })}</div>
      </div>

      <div className="tc-vdc-param-field">
        <label>{t('director.parameters.generationCount')}</label>
        <div className="tc-vdc-param-readonly">{t('director.parameters.generationCountFixed', { count: value.generation_count })}</div>
      </div>

      <button type="button" className="tc-vdc-advanced-toggle" onClick={() => setAdvancedOpen((v) => !v)}>
        {advancedOpen ? <DownOutlined /> : <RightOutlined />} {t('director.parameters.advancedToggle')}
      </button>

      {advancedOpen && (
        <div className="tc-vdc-advanced-body">
          <div className="tc-vdc-param-field">
            <label>{t('director.parameters.seed')}</label>
            <Radio.Group
              value={value.seed_mode}
              onChange={(e) => patch({ seed_mode: e.target.value })}
              options={[
                { label: t('director.parameters.seedRandom'), value: 'random' },
                { label: t('director.parameters.seedFixed'), value: 'fixed' },
              ]}
              optionType="button"
              size="small"
            />
            {value.seed_mode === 'fixed' && (
              <InputNumber
                min={seedRange.min}
                max={seedRange.max}
                value={value.seed ?? undefined}
                onChange={(v) => patch({ seed: v === null || v === undefined ? null : Number(v) })}
                placeholder={t('director.parameters.seedPlaceholder')}
                style={{ width: '100%', marginTop: 8 }}
              />
            )}
          </div>

          {hasReferenceImage && (
            <div className="tc-vdc-param-field">
              <label>{t('director.parameters.imageStrength', { value: value.image_strength.toFixed(2) })}</label>
              <Slider
                min={0}
                max={1}
                step={0.05}
                value={value.image_strength}
                onChange={(v) => patch({ image_strength: v })}
              />
            </div>
          )}

          {hasReferenceVideo && (
            <>
              <div className="tc-vdc-param-field">
                <label>{t('director.parameters.firstStrength', { value: value.first_strength.toFixed(2) })}</label>
                <Slider
                  min={0}
                  max={1}
                  step={0.05}
                  value={value.first_strength}
                  onChange={(v) => patch({ first_strength: v })}
                />
              </div>
              <div className="tc-vdc-param-field">
                <label>{t('director.parameters.lastStrength', { value: value.last_strength.toFixed(2) })}</label>
                <Slider
                  min={0}
                  max={1}
                  step={0.05}
                  value={value.last_strength}
                  onChange={(v) => patch({ last_strength: v })}
                />
              </div>
            </>
          )}

          <div className="tc-vdc-param-field">
            <div className="tc-vdc-ltx-prompt-placeholder">
              {t('director.parameters.compiledPrompt')}
              <div className="tc-vdc-param-hint">{t('director.parameters.compilerNotWired')}</div>
            </div>
          </div>
        </div>
      )}

      <div className="tc-vdc-param-footnote">{t('director.parameters.footnote')}</div>
    </div>
  )
}
